// Stores (02-DESIGN/10-state/01-stores-types-and-instances.md § the console:
// Stores; 02-the-state-service.md § a platform workload under its own key;
// 04-workloads/02-the-fleet.md § the platform's own workloads; decisions 0018
// and 0021; 01-the-console.md § the words: *a store*): the workspace's stores
// with their history policy, size and fill; *Create a store* a form in the
// list's place (01-the-console.md § conventions: a form), with its size, what it does when full and — where the platform
// profile offers three — its copies (§ size, when full, and copies; decision
// 0050). The workspace's state service is placed at its creation by the
// auth service, so the page never asks for it: while nothing serves the
// family yet, it shows the service's status as it shows any workload's and
// asks again until the service answers.

import { identity } from "@impire-io/sdk";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";

import { tenantPath } from "../../address/address.ts";
import { CODE_NO_SERVICE, isRefusal } from "../../platform/bus.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Button,
  bytes,
  Choices,
  Empty,
  Field,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  since,
  TextInput,
  useLoad,
} from "../../ui/components.tsx";
import {
  DEFAULT_BUDGET,
  HistoryPolicy,
  MIN_BUDGET,
  RESERVED,
  validStoreName,
  WhenFull,
  type Store,
  type StoreRequest,
} from "../../wire/state.ts";
import {
  listSubject,
  PlatformName,
  SERVICE as WORKLOADS,
  type ListReply,
  type Workload,
} from "../../wire/workloads.ts";
import { pageOffered } from "../../roles/offers.ts";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { NameLink } from "../../ui/ident.tsx";
import { Table, type Column, type MenuEntry } from "../../ui/table.tsx";
import { useSurface } from "../surface.tsx";
import { plainState, platformWorkload } from "../workloads/state.ts";
import { fullness, parseSize } from "./settings.ts";
import { FillText, WhenFullChoices } from "./StoreSettings.tsx";

/** POLL is how often the page asks again whether the state service answers while it starts, in milliseconds. */
export const POLL = 4000;

/** stateWorkload finds the workspace's state service among its workloads. */
export function stateWorkload(all: readonly Workload[]): Workload | undefined {
  return platformWorkload(all, PlatformName.state);
}

/** storeMenu is a store's row menu: *Delete the store*, last, asked first, as in its page's menu. */
export function storeMenu(st: Store, remove: () => void): MenuEntry[] {
  return [
    {
      label: "Delete the store",
      danger: true,
      confirm: {
        title: `Delete the store ${st.name}?`,
        consequence: `For good: every item in ${st.name} and its whole history are gone, and nothing can bring them back.`,
        verb: "Delete",
      },
      onSelect: remove,
    },
  ];
}

export function StoresPage() {
  const s = useSurface();
  const { platform } = usePage();
  const stateImage = platform.images?.[identity.Image.state] || undefined;
  const [tick, setTick] = useState(0);
  const [stores, reload] = useLoad("stores", () => s.bus.stores().listStores(), tick, { keep: true });
  const form = useFormState<"create">();
  const [mark, setMark] = useState<string | undefined>(undefined);
  const [actErr, setActErr] = useState<unknown>(undefined);
  const noService = stores.state === "refused" && isRefusal(stores.err, CODE_NO_SERVICE);
  const [workloads] = useLoad(
    `stores/workloads/${noService ? "none" : "some"}`,
    () =>
      noService
        ? s.bus.call<ListReply>(WORKLOADS, listSubject(), undefined)
        : Promise.resolve<ListReply>({ workloads: [] }),
    tick,
    { keep: true },
  );
  const service = workloads.state === "ready" ? stateWorkload(workloads.value.workloads) : undefined;

  // While nothing serves the stores, ask again until the service answers.
  useEffect(() => {
    if (!noService) {
      return;
    }
    const t = setTimeout(() => {
      setTick((n) => n + 1);
    }, POLL);
    return () => {
      clearTimeout(t);
    };
  }, [noService, tick]);

  // *Delete the store* is the last entry of a store's row menu, as of its
  // page's menu, asked first (01-the-console.md § conventions: a row's actions; remove).
  const menu = (st: Store): MenuEntry[] =>
    storeMenu(st, () => {
      const store = st.name;
      setActErr(undefined);
      s.bus
        .stores()
        .deleteStore(store)
        .then(
          () => {
            reload();
          },
          (e: unknown) => {
            setActErr(e);
          },
        );
    });
  const create = (
    <Button
      kind="primary"
      onClick={() => {
        form.show("create");
      }}
      disabled={stores.state !== "ready"}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Create a store
    </Button>
  );
  if (form.open === "create" && stores.state === "ready") {
    return (
      <CreateStore
        onClose={form.close}
        onCreated={(st) => {
          // Back to the list, the new store marked (01-the-console.md § conventions: add).
          form.close();
          setMark(st.name);
          reload();
        }}
      />
    );
  }
  return (
    <>
      <PageHead
        title="Stores"
        line={`Where ${s.name}'s workloads keep their things: each a history with a size.`}
        action={create}
      />
      {stores.state === "pending" && <Pending what="Asking for the stores" />}
      {actErr !== undefined && <NotAllowed err={actErr} />}
      {stores.state === "refused" && !noService && <NotAllowed err={stores.err} what="Stores" />}
      {noService && service !== undefined && (
        <Notice title="The store service is not answering yet">
          It runs as a workload of {s.name}&apos;s own,{" "}
          {pageOffered(s.role, "workloads") ? (
            <NameLink to={tenantPath(s.tenant, "workloads", service.id)}>{service.name}</NameLink>
          ) : (
            service.name
          )}
          , now <Pill tone={plainState(service).tone}>{plainState(service).word}</Pill>. Creating a store is
          offered the moment it answers.
        </Notice>
      )}
      {noService && service === undefined && workloads.state !== "pending" && (
        <Notice title="The store service is not running">
          {stateImage === undefined
            ? `This install publishes no image for the service that keeps ${s.name}'s stores. Ask whoever runs the install.`
            : `${s.name}'s store service is placed when the workspace is created, and it is not among ${s.name}'s workloads yet. Creating a store is offered the moment it answers.`}
        </Notice>
      )}
      {stores.state === "ready" && stores.value.length === 0 && (
        <Empty action={create}>No stores yet. A store is a named history with a size; create one.</Empty>
      )}
      {stores.state === "ready" && stores.value.length > 0 && (
        <Table
          label="Stores"
          rows={stores.value}
          rowKey={(st) => st.name}
          sort={{ key: "store", dir: "asc" }}
          to={(st) => tenantPath(s.tenant, "stores", st.name)}
          columns={columns}
          menu={menu}
          mark={mark}
        />
      )}
    </>
  );
}

/** historyWords is a store's history policy in a person's words. */
function historyWords(st: Store): string {
  return st.history === HistoryPolicy.full ? "kept whole" : "compactable";
}

/** The list's columns: the name with its description, the history policy, the fill against the size, since when. */
const columns: Column<Store>[] = [
  {
    key: "store",
    header: "Store",
    width: "minmax(0, 1.6fr)",
    value: (st) => st.name,
    text: (st) => `${st.name} ${st.description ?? ""}`,
    cell: (st) => (
      <span className="cell">
        <span className="cell-main">{st.name}</span>
        <span className="cell-sub">{st.description ?? ""}</span>
      </span>
    ),
  },
  {
    key: "history",
    header: "History",
    width: "130px",
    facet: "Kind",
    value: historyWords,
    cell: (st) => <Pill>{historyWords(st)}</Pill>,
  },
  {
    key: "used",
    header: "Used",
    narrow: false,
    value: (st) => st.fill,
    text: (st) =>
      `${bytes(st.fill)} of ${bytes(st.budget)}${fullness(st) === "room" ? "" : ` ${fullness(st) === "full" ? "full" : "near full"}`}`,
    cell: (st) => (
      <span className="small muted">
        <FillText st={st} />
      </span>
    ),
  },
  {
    key: "created",
    header: "Created",
    narrow: false,
    value: (st) => st.created,
    text: (st) => since(st.created),
    cell: (st) => <span className="small muted">{since(st.created) || "—"}</span>,
  },
];

function CreateStore({ onClose, onCreated }: { onClose: () => void; onCreated: (st: Store) => void }) {
  const s = useSurface();
  const { platform } = usePage();
  const offersThree = identity.offersReplicas(platform, 3);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [history, setHistory] = useState<string>(HistoryPolicy.compactable);
  const [size, setSize] = useState("");
  const [whenFull, setWhenFull] = useState<WhenFull>(WhenFull.refuse);
  const [copies, setCopies] = useState("1");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const trimmed = name.trim();
  const nameOK = validStoreName(trimmed);
  const sizeN = size.trim() === "" ? undefined : parseSize(size);
  const sizeOK = size.trim() === "" || (sizeN !== undefined && sizeN >= MIN_BUDGET);
  const ready = nameOK && sizeOK;
  const submit = () => {
    if (busy || !ready) {
      return;
    }
    const req: StoreRequest = {};
    if (description.trim() !== "") {
      req.description = description.trim();
    }
    if (history === HistoryPolicy.full) {
      req.history = HistoryPolicy.full;
    }
    if (sizeN !== undefined) {
      req.budget = sizeN;
    }
    if (whenFull === WhenFull.compact && history !== HistoryPolicy.full) {
      req.when_full = WhenFull.compact;
    }
    if (offersThree && copies === "3") {
      req.replicas = 3;
    }
    setErr(undefined);
    setBusy(true);
    s.bus
      .stores()
      .createStore(trimmed, req)
      .then(
        (st) => {
          setBusy(false);
          onCreated(st);
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };
  return (
    <FormPage
      title="Create a store"
      back="Stores"
      line="A named history with a size."
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || !ready}>
            {busy ? "Creating…" : "Create"}
          </Button>
        </>
      }
    >
      <p className="muted">
        Every workload and every member of {s.name} may write to it and read it; the types you define say what
        its items look like.
      </p>
      {err !== undefined && <NotAllowed err={err} what="Create" />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field
          label="Name"
          hint={
            nameOK || trimmed === ""
              ? "Lowercase letters, digits and hyphens; it cannot be renamed."
              : RESERVED.includes(trimmed)
                ? `${trimmed} is a word the platform keeps for itself.`
                : "Lowercase letters, digits and hyphens only."
          }
        >
          <TextInput value={name} onChange={setName} placeholder="invoices" mono required autoFocus />
        </Field>
        <Field label="Description" hint="For people.">
          <TextInput value={description} onChange={setDescription} />
        </Field>
        <div className="field">
          <span className="label">History</span>
          <Choices
            name="history"
            value={history}
            onChange={(v) => {
              setHistory(v);
              if (v === HistoryPolicy.full) {
                setWhenFull(WhenFull.refuse);
              }
            }}
            options={[
              {
                value: HistoryPolicy.compactable,
                label: "Compactable",
                hint: "An item's older changes may be folded into a snapshot to save room. The usual choice.",
              },
              {
                value: HistoryPolicy.full,
                label: "Kept whole",
                hint: "Nothing ever shrinks the history: an audit trail. Cannot be changed later.",
              },
            ]}
          />
        </div>
        <Field
          label="Size"
          hint={
            sizeOK
              ? `How much history the store may hold, such as 256 MiB or 1 GiB; ${bytes(DEFAULT_BUDGET)} when empty. It is set aside from ${s.name}'s storage the moment the store is created, and can be changed later.`
              : `A size such as 256 MiB or 1 GiB; at least ${bytes(MIN_BUDGET)}.`
          }
        >
          <TextInput value={size} onChange={setSize} placeholder={bytes(DEFAULT_BUDGET)} />
        </Field>
        <div className="field">
          <span className="label">When full</span>
          <WhenFullChoices value={whenFull} onChange={setWhenFull} full={history === HistoryPolicy.full} />
        </div>
        {offersThree && (
          <div className="field">
            <span className="label">Copies</span>
            <Choices
              name="copies"
              value={copies}
              onChange={setCopies}
              options={[
                { value: "1", label: "One copy", hint: "The usual choice." },
                {
                  value: "3",
                  label: "Three copies",
                  hint: "Kept on three servers, so the store stays readable and writable while one is down. Sets its size aside three times. Cannot be changed later.",
                },
              ]}
            />
          </div>
        )}
      </form>
    </FormPage>
  );
}
